import { SvgTune } from '@loomhq/lens';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  endThumbnailFlow,
  endVideoSettingsFlow,
  startVideoSettingsFlow,
} from 'actions/editor';
import { requestVideoPlayerLeaveFullScreen } from 'actions/videoPlayer';
import { toggleViewerInsightsUI } from 'actions/viewerInsights';
import {
  VIDEO_EDIT_OPTIONS_ACCESS,
  VIDEO_SETTINGS_ACCESS,
} from 'constants/scopes';
import { clearCTAWidgetState } from 'creators/editor';
import { handleToggleTranscriptCreator } from 'creators/shareVideo';

import {
  selectCtaFlow,
  selectThumbnailFlow,
  selectVideoSettingsFlow,
} from 'selectors/editor';
import {
  selectFullscreen,
  selectTranscriptVisible,
} from 'selectors/videoPlayer';
import { selectIsActive } from 'selectors/viewerInsights';

import AddVideoCtaButton from '../add-video-cta-button';
import CollapsibleSideButton from '../collapsible-side-button';
import CustomThumbnailButton from '../custom-thumbnail-button';
import TranscriptOverlayButton from '../transcript-overlay-button';
import TrimModeButton from '../trim-mode-button';
import VideoButtons from '../video-buttons';

import Scopes from 'components/scopes';

const EditVideoButtons = ({
  // collapsed = only icons are showing, text is hidden
  // collapsible = can change state into collapsed state (false = force uncollapsed)
  // hidden = this component renders nothing
  isCollapsible = false,
  isHidden,
  onEnterTrimMode,
  startCollapsed = false,
  waitingForTrim = false,
}) => {
  const dispatch = useDispatch();

  const areViewerInsightsOpen = useSelector(selectIsActive);
  const isVideoPlayerFullScreen = useSelector(selectFullscreen);
  const areThumbnailSettingsOpen = useSelector(selectThumbnailFlow);
  const areVideoCtaSettingsOpen = useSelector(selectCtaFlow);
  const areVideoSettingsOpen = useSelector(selectVideoSettingsFlow);
  const isTranscriptVisible = useSelector(selectTranscriptVisible);

  const handleOverlayButtonClick = useCallback(() => {
    if (areViewerInsightsOpen) {
      dispatch(toggleViewerInsightsUI());
    }

    if (areVideoSettingsOpen) {
      dispatch(endVideoSettingsFlow());
    }

    if (areVideoCtaSettingsOpen) {
      dispatch(clearCTAWidgetState());
    }

    if (areThumbnailSettingsOpen) {
      dispatch(endThumbnailFlow());
    }
  }, [
    dispatch,
    areViewerInsightsOpen,
    areVideoSettingsOpen,
    areVideoCtaSettingsOpen,
    areThumbnailSettingsOpen,
  ]);

  return (
    <VideoButtons
      startCollapsed={startCollapsed}
      isCollapsible={isCollapsible}
      isHidden={isHidden}
    >
      {/* VideoButtons uses the Function As Child Component (FaCC) pattern */}
      {({ isCollapsed }) => (
        <div className="p:xsmall">
          <TranscriptOverlayButton
            isCollapsed={isCollapsed}
            onOverlayButtonClick={handleOverlayButtonClick}
          />
          <Scopes name={VIDEO_SETTINGS_ACCESS}>
            <CollapsibleSideButton
              onClick={() => {
                if (areViewerInsightsOpen) {
                  dispatch(toggleViewerInsightsUI());
                }

                if (areVideoCtaSettingsOpen) {
                  dispatch(clearCTAWidgetState());
                }

                if (areThumbnailSettingsOpen) {
                  dispatch(endThumbnailFlow());
                }

                if (isTranscriptVisible) {
                  dispatch(handleToggleTranscriptCreator());
                }

                // we only request leaving full screen if already full screen,
                // otherwise we queue up the request to leave full screen
                if (isVideoPlayerFullScreen) {
                  dispatch(requestVideoPlayerLeaveFullScreen());
                  dispatch(startVideoSettingsFlow());
                } else {
                  dispatch(
                    areVideoSettingsOpen
                      ? endVideoSettingsFlow()
                      : startVideoSettingsFlow()
                  );
                }
              }}
              icon={<SvgTune style={{ transform: 'rotate(90deg)' }} />}
              isCollapsed={isCollapsed}
            >
              Video preferences
            </CollapsibleSideButton>
          </Scopes>
          <Scopes name={VIDEO_EDIT_OPTIONS_ACCESS}>
            <>
              <TrimModeButton
                onClick={onEnterTrimMode}
                isCollapsed={isCollapsed}
              />
              {!waitingForTrim && (
                <>
                  <AddVideoCtaButton
                    isCollapsed={isCollapsed}
                    isTranscriptVisible={isTranscriptVisible}
                  />
                  <CustomThumbnailButton
                    isCollapsed={isCollapsed}
                    isTranscriptVisible={isTranscriptVisible}
                  />
                </>
              )}
            </>
          </Scopes>
        </div>
      )}
    </VideoButtons>
  );
};

export default EditVideoButtons;
